import { getDelayAttribution } from './delayAttribution';
import { normalizeStage } from './status';

const CLOSED_STAGES = new Set(['archived', 'delivered', 'cancelled', 'canceled', 'complete', 'completed']);
const DRIVER_WAIT_STAGES = new Set(['new', 'awaiting_store', 'ready_for_driver', 'ready', 'driver_assigned', 'packing', 'preparing']);
const ROUTE_STAGES = new Set(['out_for_delivery', 'on_route', 'out for delivery']);

function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function text(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function asMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function minutesSince(value, now = Date.now()) {
  const millis = asMillis(value);
  if (!millis) return 0;
  return Math.max(0, Math.floor((now - millis) / 60000));
}

function orderStage(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  return normalizeStage(order.stage || order.status || order.subStatus || order.storeStatus || 'new');
}

function orderNo(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  return text(order.orderNumber, order.orderNo, order.displayId, order.id, 'Order');
}

function storeName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return text(order.store, order.storeName, raw.store, raw.storeName, raw.site, order.site, 'Unassigned store');
}

function customerName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return text(order.customerName, raw.customerName, raw.customer?.name, order.customer?.name, 'Customer');
}

function assignedDriverName(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return text(order.assignedDriverName, order.assignedDriver, order.driverName, order.assignment?.driverName, raw.assignedDriverName, raw.assignment?.driverName);
}

function orderCreatedAt(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};
  return order.createdAt || order.placedAt || timings.createdAt || raw.createdAt || raw.placedAt || order.updatedAt || raw.updatedAt;
}

function stageStartedAt(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  const timings = order.timings || raw.timings || {};
  return (
    timings.stageStartedAt ||
    timings.readyForDriverAt ||
    timings.readyAt ||
    timings.packingStartedAt ||
    timings.packedAt ||
    timings.sentToStoreAt ||
    timings.routedAt ||
    order.updatedAt ||
    raw.updatedAt ||
    orderCreatedAt(order)
  );
}

function itemName(item = {}) {
  return text(item.name, item.productName, item.title, item.sku, 'Item');
}

function orderItems(order = {}) {
  order = order && typeof order === 'object' ? order : {};
  order = order && typeof order === 'object' ? order : {};
  const raw = order.raw || {};
  return safeArray(order.items).length ? safeArray(order.items) : safeArray(raw.items);
}

function driverName(driver = {}) {
  return text(driver.name, driver.displayName, driver.driverName, driver.id, 'Driver');
}

function driverStore(driver = {}) {
  return text(driver.store, driver.baseStore, driver.site, driver.currentStore);
}

function driverLoad(driver = {}) {
  return Number(driver.activeRuns ?? driver.load ?? driver.runCount ?? driver.currentRuns ?? 0) || 0;
}

function isDriverOnline(driver = {}) {
  const status = String(driver.status || driver.availability || '').toLowerCase();
  return !status.includes('offline') && !status.includes('inactive') && !status.includes('disabled');
}

function isDriverAvailable(driver = {}) {
  const status = String(driver.status || driver.availability || '').toLowerCase();
  return isDriverOnline(driver) && (driverLoad(driver) === 0 || status.includes('available') || status.includes('online'));
}

export function isLiveLinkOrder(order = {}) {
  if (!order) return false;
  const stage = orderStage(order);
  return !order.archived && !order.cancelled && !order.delivered && !CLOSED_STAGES.has(stage);
}

export function getLiveLinkOrders(orders = []) {
  return safeArray(orders).filter(isLiveLinkOrder);
}

export function scoreLinkOrder(order = {}, options = {}) {
  const now = options.now || Date.now();
  const stage = orderStage(order);
  const delay = order.delay || getDelayAttribution(order);
  const ageMinutes = minutesSince(orderCreatedAt(order), now);
  const stageMinutes = minutesSince(stageStartedAt(order), now);
  const noDriver = DRIVER_WAIT_STAGES.has(stage) && !assignedDriverName(order);
  const onRouteLong = ROUTE_STAGES.has(stage) && stageMinutes >= 30;
  const flags = order.flags || order.raw?.flags || {};
  const stockRisk = Boolean(flags.hasStockIssue || flags.stockIssue || order.issueFlag);
  const approvalRisk = Boolean(flags.requiresApproval || flags.requiresCustomerContact || flags.paymentIssue);
  const slaRisk = delay?.severity === 'critical' || stageMinutes >= 30 || ageMinutes >= 45;
  const warningRisk = delay?.severity === 'warning' || stageMinutes >= 15 || ageMinutes >= 30;

  let priority = 20;
  const reasons = [];

  if (stockRisk) {
    priority += 30;
    reasons.push('Stock or product issue flagged');
  }

  if (approvalRisk) {
    priority += 25;
    reasons.push('Operator approval/customer contact required');
  }

  if (noDriver) {
    priority += 25;
    reasons.push('No driver assigned');
  }

  if (slaRisk) {
    priority += 30;
    reasons.push('SLA breach risk is critical');
  } else if (warningRisk) {
    priority += 15;
    reasons.push('SLA warning threshold approaching');
  }

  if (onRouteLong) {
    priority += 20;
    reasons.push('Delivery run is taking too long');
  }

  if (stage === 'issue') {
    priority += 25;
    reasons.push('Order is already in issue state');
  }

  priority += Math.min(15, Math.floor(ageMinutes / 10));
  priority = Math.max(0, Math.min(100, priority));

  const severity = priority >= 75 ? 'critical' : priority >= 45 ? 'attention' : 'healthy';

  return {
    orderId: order.id,
    orderNumber: orderNo(order),
    severity,
    priority,
    stage,
    store: storeName(order),
    customer: customerName(order),
    ageMinutes,
    stageMinutes,
    noDriver,
    stockRisk,
    approvalRisk,
    slaRisk,
    warningRisk,
    reasons,
    delay,
  };
}

export function sortByLinkPriority(orders = [], options = {}) {
  return [...safeArray(orders)].sort((a, b) => {
    const scoreA = scoreLinkOrder(a, options);
    const scoreB = scoreLinkOrder(b, options);
    return scoreB.priority - scoreA.priority || scoreB.stageMinutes - scoreA.stageMinutes;
  });
}

export function summarizeLinkOrders(orders = [], options = {}) {
  const live = getLiveLinkOrders(orders);
  const scores = live.map((order) => scoreLinkOrder(order, options));
  return {
    all: live.length,
    critical: scores.filter((score) => score.severity === 'critical').length,
    attention: scores.filter((score) => score.severity === 'attention').length,
    healthy: scores.filter((score) => score.severity === 'healthy').length,
    scores,
  };
}

export function buildStoreSignals(orders = [], drivers = [], options = {}) {
  const now = options.now || Date.now();
  const live = getLiveLinkOrders(orders);
  const stores = new Set(options.stores || []);

  live.forEach((order) => stores.add(storeName(order)));
  safeArray(drivers).forEach((driver) => {
    const store = driverStore(driver);
    if (store) stores.add(store);
  });

  return [...stores].filter(Boolean).sort((a, b) => a.localeCompare(b)).map((store) => {
    const storeOrders = live.filter((order) => storeName(order) === store);
    const orderScores = storeOrders.map((order) => scoreLinkOrder(order, { now }));
    const onlineDrivers = safeArray(drivers).filter((driver) => isDriverOnline(driver) && (!driverStore(driver) || driverStore(driver) === store));
    const availableDrivers = onlineDrivers.filter(isDriverAvailable);
    const criticalOrders = orderScores.filter((score) => score.severity === 'critical').length;
    const attentionOrders = orderScores.filter((score) => score.severity === 'attention').length;
    const loadScore = Math.min(100, (storeOrders.length * 12) + (criticalOrders * 18) + (attentionOrders * 10) - (availableDrivers.length * 8));
    const status = loadScore >= 70 || criticalOrders ? 'high' : loadScore >= 40 || attentionOrders ? 'attention' : 'good';

    return {
      store,
      activeOrders: storeOrders.length,
      criticalOrders,
      attentionOrders,
      onlineDrivers: onlineDrivers.length,
      availableDrivers: availableDrivers.length,
      loadScore: Math.max(0, loadScore),
      status,
    };
  });
}

export function buildDriverSignals(drivers = []) {
  return safeArray(drivers).map((driver) => ({
    id: driver.id || driver.uid || driverName(driver),
    name: driverName(driver),
    store: driverStore(driver),
    online: isDriverOnline(driver),
    available: isDriverAvailable(driver),
    activeRuns: driverLoad(driver),
    etaMinutes: Number(driver.etaMinutes ?? driver.eta ?? 0) || 0,
    raw: driver,
  }));
}

function bestAvailableDriver(driverSignals = [], preferredStore = '') {
  return driverSignals
    .filter((driver) => driver.online && driver.available)
    .sort((a, b) => {
      const storeA = preferredStore && a.store === preferredStore ? -1 : 0;
      const storeB = preferredStore && b.store === preferredStore ? -1 : 0;
      return storeA - storeB || a.activeRuns - b.activeRuns || a.etaMinutes - b.etaMinutes;
    })[0] || driverSignals.find((driver) => driver.online);
}

function healthiestStore(storeSignals = [], currentStore = '') {
  return [...storeSignals]
    .filter((store) => store.store !== currentStore)
    .sort((a, b) => a.loadScore - b.loadScore || b.availableDrivers - a.availableDrivers)[0];
}

function withDecisionFields(suggestion) {
  return {
    recommendedAction: suggestion.actionLabel,
    reason: suggestion.problem,
    evidence: suggestion.signal,
    expectedImpact: suggestion.impact,
    ...suggestion,
  };
}

export function generateLinkSmartSuggestions({ order, orders = [], drivers = [], stores = [], now = Date.now() } = {}) {
  if (!order) return [];

  const orderScore = scoreLinkOrder(order, { now });
  const storeSignals = buildStoreSignals(orders, drivers, { stores, now });
  const driverSignals = buildDriverSignals(drivers);
  const currentStore = orderScore.store;
  const currentStoreSignal = storeSignals.find((store) => store.store === currentStore);
  const backupStore = healthiestStore(storeSignals, currentStore);
  const candidateDriver = bestAvailableDriver(driverSignals, currentStore);
  const items = orderItems(order);
  const suggestions = [];

  if (orderScore.noDriver) {
    suggestions.push({
      id: 'assign-driver',
      type: 'assign_driver',
      severity: orderScore.severity === 'critical' ? 'critical' : 'attention',
      title: candidateDriver ? `Assign ${candidateDriver.name}` : 'Review driver assignment',
      problem: `${orderScore.orderNumber} has no assigned driver.`,
      signal: candidateDriver
        ? `${candidateDriver.name} is the strongest available driver signal${candidateDriver.store ? ` near ${candidateDriver.store}` : ''}.`
        : 'No available driver signal is currently strong enough.',
      impact: `Customer has been waiting ${orderScore.ageMinutes}m. Stage timer is ${orderScore.stageMinutes}m.`,
      actionLabel: candidateDriver ? `Assign ${candidateDriver.name}` : 'Open drivers',
      payload: { driverId: candidateDriver?.id, driverName: candidateDriver?.name },
      priority: orderScore.priority + 5,
    });
  }

  if ((currentStoreSignal?.status === 'high' || orderScore.slaRisk) && backupStore) {
    suggestions.push({
      id: 'move-store',
      type: 'move_store',
      severity: currentStoreSignal?.status === 'high' ? 'critical' : 'attention',
      title: `Move to ${backupStore.store}`,
      problem: `${currentStore} pressure is ${currentStoreSignal?.status || 'unknown'} with ${currentStoreSignal?.activeOrders || 0} active orders.`,
      signal: `${backupStore.store} has ${backupStore.activeOrders} active orders and ${backupStore.availableDrivers} available driver signals.`,
      impact: 'Moving the order can reduce queue pressure and recover SLA before delays spread.',
      actionLabel: `Move to ${backupStore.store}`,
      payload: { targetStore: backupStore.store },
      priority: orderScore.priority,
    });
  }

  const riskyItem = items.find((item) => {
    const status = String(item.status || item.itemStatus || '').toLowerCase();
    return status.includes('unavailable') || status.includes('swap') || status.includes('pending');
  }) || items[0];

  if (riskyItem && (orderScore.stockRisk || orderScore.severity !== 'healthy')) {
    suggestions.push({
      id: 'product-decision',
      type: 'swap_product',
      severity: orderScore.stockRisk ? 'critical' : 'attention',
      title: `Check ${itemName(riskyItem)}`,
      problem: `${itemName(riskyItem)} may need confirmation, swap, or store support.`,
      signal: backupStore ? `${backupStore.store} is the current backup store candidate.` : 'No backup store signal available yet.',
      impact: 'Keeps the operator inside Control Tower instead of chasing product updates manually.',
      actionLabel: 'Approve swap',
      payload: { itemName: itemName(riskyItem), targetStore: backupStore?.store },
      priority: orderScore.priority - 5,
    });
  }

  if (!suggestions.length) {
    suggestions.push({
      id: 'monitor',
      type: 'monitor',
      severity: 'healthy',
      title: 'Keep monitoring',
      problem: `${orderScore.orderNumber} is currently on track.`,
      signal: 'No critical store, driver, stock, or SLA signal is active.',
      impact: 'No operator intervention is required right now.',
      actionLabel: 'Send update',
      payload: {},
      priority: orderScore.priority,
    });
  }

  return suggestions
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 4)
    .map(withDecisionFields);
}

export function buildLinkDecisionContext({ orders = [], drivers = [], stores = [], selectedOrderId = '', now = Date.now() } = {}) {
  const liveOrders = getLiveLinkOrders(orders);
  const sortedOrders = sortByLinkPriority(liveOrders, { now });
  const selectedOrder = sortedOrders.find((order) => order.id === selectedOrderId) || sortedOrders[0] || null;
  const orderScores = new Map(sortedOrders.map((order) => [order.id, scoreLinkOrder(order, { now })]));
  const summary = summarizeLinkOrders(sortedOrders, { now });
  const storeSignals = buildStoreSignals(sortedOrders, drivers, { stores, now });
  const driverSignals = buildDriverSignals(drivers);
  const suggestions = generateLinkSmartSuggestions({ order: selectedOrder, orders: sortedOrders, drivers, stores, now });

  return {
    now,
    liveOrders: sortedOrders,
    selectedOrder,
    orderScores,
    summary,
    storeSignals,
    driverSignals,
    suggestions,
  };
}
